/**
 * Risk ▸ Master Data metinleri (K2; ADR 0086): şirketin risk ana verisi, kütüphane Excel'inin içe
 * aktarılması ve gemi RA formunda kütüphaneden başlangıç. Adlar ortağın belgelerindeki gibi
 * İngilizce (FD §3, XL 01–08); Türkçe ikinci dil.
 */

export const riskMasterEn = {
  title: 'Risk Master Data',
  description:
    'Company risk library: operation areas, activities, hazards, consequences, controls and activity–hazard risk rows. Written in the office, read only on board.',
  shipNote:
    'Master data is maintained by the office and is read only on board. Start a vessel risk assessment from these activities in Risk Assessments.',
  stats: {
    activities: 'Activities',
    activitiesNote: '{{active}} active · {{draft}} draft',
    hazards: 'Hazards',
    hazardsNote: '{{count}} hazard families',
    controls: 'Controls',
    controlsNote: '{{existing}} existing · {{additional}} additional',
    links: 'Risk Rows',
    linksNote: '{{high}} at High or above',
  },
  tabs: {
    queue: 'Review Queue',
    categories: 'Operation Areas',
    activities: 'Activities',
    hazards: 'Hazards',
    consequences: 'Consequences',
    controls: 'Controls',
    links: 'Risk Rows',
  },
  tableLabel: '{{tab}} table',
  search: 'Search code or name',
  filter: {
    status: 'Status',
    allStatuses: 'All Statuses',
    group: 'Vessel Type',
    allGroups: 'All Vessel Types',
    category: 'Operation Area',
    allCategories: 'All Operation Areas',
  },
  columns: {
    review: 'Technical Review',
    code: 'Code',
    name: 'Name',
    area: 'Operation Area',
    groups: 'Vessel Type',
    steps: 'Steps',
    types: 'RA Type',
    status: 'Status',
    family: 'Family',
    category: 'Category',
    consequences: 'Consequences',
    rows: 'Risk Rows',
    stage: 'Stage',
    hierarchy: 'Hierarchy',
    measure: 'Control Measure',
    hazard: 'Hazard',
    activity: 'Activity',
    initial: 'Initial Risk',
    residual: 'Residual Risk',
    controls: 'Controls',
    controlStatus: 'Control Status',
    updated: 'Updated',
    actions: 'Actions',
  },
  showing: 'Showing {{from}} to {{to}} of {{total}} entries',
  pagination: 'Pages',
  statuses: { draft: 'Draft', active: 'Active', retired: 'Retired' },
  groups: {
    ALL: 'All vessels',
    CARGO: 'Cargo',
    TANKER: 'Tanker',
    YACHT_PASSENGER: 'Yacht / Passenger',
    OFFSHORE: 'Offshore',
  },
  hazardCategories: {
    mechanical: 'Mechanical',
    energy: 'Energy',
    electrical: 'Electrical',
    chemical: 'Chemical',
    environmental: 'Environmental',
    human: 'Human',
    ergonomic: 'Ergonomic',
    navigation: 'Navigation',
    fire: 'Fire',
    other: 'Other',
  },
  hierarchy: {
    elimination: 'Elimination',
    substitution: 'Substitution',
    engineering: 'Engineering',
    administrative: 'Administrative',
    ppe: 'PPE',
  },
  stages: { existing: 'Existing', additional: 'Additional' },
  natures: { preventive: 'Preventive', mitigative: 'Mitigative' },
  requirements: { mandatory: 'Mandatory', conditional: 'Conditional' },
  controlStatuses: {
    not_verified: 'Not verified',
    implemented: 'Implemented',
    not_implemented: 'Not implemented',
    na_reviewed: 'N/A – reviewed',
  },
  assessmentTypes: {
    routine: 'Routine',
    task_specific: 'Task-specific',
    dynamic: 'Dynamic',
    change_related: 'Change-related',
  },
  notAssessed: 'Not assessed',
  review: {
    title: 'Technical Review (FINAL)',
    statuses: {
      draft: 'Draft – not approved',
      in_review: 'In review',
      approved: 'Approved',
      rejected: 'Rejected',
      superseded: 'Superseded',
    },
    approvedNote:
      'Approved templates may be offered to vessels; a job RA is still required on board.',
    notApprovedNote:
      'Visible in the office only. Not offered to vessels as a pre-reviewed template until approved.',
    node: 'Parent node',
    applicability: 'Vessel applicability',
    applicabilityReview: 'review required',
    pmsReview: 'PMS review',
    ptwScreening: 'PTW screening',
    reviewQueue: 'Technical review queue',
    templateStatus: 'Template status (file)',
    source: 'Original source',
    reviewer: 'Reviewer',
    reviewerHelp: 'Leave blank to record your own name.',
    reviewDay: 'Review date',
    reviewDayHelp: 'Leave blank for today.',
    note: 'Note',
    noteHelp: 'Required for a rejection.',
    actions: {
      in_review: 'Mark In Review',
      superseded: 'Supersede',
      rejected: 'Reject',
      approved: 'Approve',
    },
    saved: '{{code}}: {{status}}.',
  },
  queue: {
    title: 'Review Queue',
    help: 'FINAL 03 decision queue: duplicates and hazard reviews. Record the decision, reviewer and date. Nothing is merged automatically.',
    search: 'Search code, activity or reason',
    type: 'Issue type',
    status: 'Status',
    allTypes: 'All issue types',
    allStatuses: 'All statuses',
    types: { duplicate: 'Duplicate', hazard_review: 'Hazard review' },
    statuses: { open: 'Open', closed: 'Closed' },
    columns: {
      code: 'Code',
      type: 'Issue',
      activity: 'Activity',
      reason: 'Reason',
      related: 'Related activities',
      status: 'Status',
      decision: 'Decision',
    },
    open: 'Open {{code}}',
    noMatch: 'No review item matches the filter.',
    empty: { title: 'No review items', description: 'Import the FINAL package to load the queue.' },
    requiredAction: 'Required decision / action',
    noMerge:
      'Recording a decision does not merge or delete activities. If a merge is decided, retire the duplicate in Master Data; IDs are kept.',
    decision: 'Decision',
    reviewer: 'Reviewer',
    reviewerHelp: 'Leave blank to record your own name.',
    reviewDay: 'Date',
    close: 'Close',
    reopen: 'Reopen',
    close_task: 'Save Decision and Close',
    saved: '{{code}}: decision saved.',
  },
  none: '—',
  actions: {
    new: 'New',
    import: 'Import Library (Excel)',
    defaultLibrary: 'Default Library',
    activateDrafts: 'Activate Drafts ({{count}})',
    activated: '{{count}} records activated.',
    open: 'Open {{code}}',
    edit: 'Edit',
    retire: 'Retire',
    reactivate: 'Reactivate',
    activate: 'Activate',
    retired: '{{code}} retired. It stays in the library and in the history.',
    reactivated: '{{code}} is active again.',
  },
  empty: {
    title: 'No master data yet',
    office: 'Import the company library from Excel or add the first record.',
    ship: 'The office has not published any risk master data yet.',
    noMatch: 'No records match the filters',
    error: 'Master data could not be loaded',
  },
  form: {
    newTitle: 'New {{kind}}',
    editTitle: '{{code}}',
    viewTitle: '{{code}}',
    code: 'Code',
    codeHelp: 'Natural key used by the Excel import. It cannot be changed later.',
    name: 'Name',
    category: 'Operation Area',
    scope: 'Task / Work Scope',
    steps: 'Task Steps',
    stepN: 'Step {{n}}',
    addStep: 'Add Step',
    removeStep: 'Remove step {{n}}',
    groups: 'Vessel Type',
    types: 'RA Type',
    ownerRole: 'Owner',
    status: 'Status',
    family: 'Hazard Family Code',
    hazardCategory: 'Hazard Category',
    suggested: 'Suggested: {{category}}',
    useSuggestion: 'Use suggestion',
    mechanism: 'Mechanism',
    consequences: 'Potential Consequences',
    exposed: 'Exposed Persons / Assets',
    hazard: 'Hazard',
    stage: 'Stage',
    measure: 'Control Measure',
    hierarchy: 'Control Hierarchy',
    hierarchyHelp: 'The first selected value is the primary one.',
    nature: 'Preventive / Mitigative',
    requirement: 'Mandatory / Conditional',
    verificationMethod: 'Verification Method',
    evidence: 'Evidence',
    responsibleRole: 'Responsible Role',
    activity: 'Activity',
    step: 'Task Step',
    noStep: 'Whole activity',
    initial: 'Initial Risk',
    likelihood: 'Likelihood',
    severity: 'Severity',
    residual: 'Residual Risk',
    residualHelp:
      'Leave blank until the controls are implemented and verified. A residual risk cannot be entered while the controls are “Not verified”.',
    existingControls: 'Existing Controls',
    additionalControls: 'Additional Controls',
    controlStatus: 'Control Status',
    usedIn: 'Used in {{count}} risk rows',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    saved: '{{code}} saved.',
    readOnly: 'Read only on board.',
  },
  import: {
    title: 'Import Risk Library (Excel)',
    help: 'Upload the company library workbook (sheets 01 Areas to 08 Vessel Types, or the FINAL hand-off package with FINAL 01–03). Records are matched by their codes: the same file imported again writes nothing. Nothing is deleted; records missing from the file are listed.',
    file: 'Choose Excel File',
    fileName: 'File: {{name}}',
    preview: 'Preview',
    apply: 'Import',
    cancel: 'Close',
    summary: 'Preview',
    data: 'Data',
    add: 'New',
    update: 'Updated',
    same: 'Unchanged',
    errors: 'Errors ({{count}}): nothing will be imported until they are fixed in the file.',
    warnings: 'Warnings ({{count}})',
    missing: 'In the library but not in the file ({{count}}): kept, not deleted.',
    changes: 'Changed fields',
    more: '… and {{count}} more',
    issue: '{{sheet}} · row {{row}} · {{field}}: {{message}}',
    matrix: {
      current: 'Risk bands (07) match the matrix in use: {{name}}.',
      other:
        'Risk bands (07) match the “{{name}}” profile, which is not in use. Change it in Settings ▸ Risk Matrix if required.',
      not_found:
        'Risk bands (07) match no risk matrix profile. The matrix is not imported; publish it in Settings ▸ Risk Matrix if required.',
    },
    statusNote:
      'New records arrive with the status in the file (Draft). Statuses and verified residual risks already set in the office are kept.',
    applied: 'Library imported. New {{add}}, updated {{update}}.',
    nothingToDo: 'The library is already up to date: nothing to import.',
    final: {
      kinds: {
        categories: 'Areas (Area ID)',
        activities: 'Activities (TASK)',
        hazards: 'Hazards',
        consequences: 'Consequences',
        controls: 'Proposed controls',
        links: 'Risk candidates',
      },
      queue: 'Review queue (FINAL 03)',
      summary:
        'FINAL hand-off package: {{nodes}} parent nodes (grouping only, not selectable); {{review}} activities need a vessel applicability review.',
      reopened: '{{count}} approved activities changed and return to review: {{codes}}.',
      note: 'Everything arrives as a draft for shore technical review: nothing is approved by the file, likelihood and severity stay blank (no defaults) and duplicates are not merged. IDs are kept; nothing is deleted.',
    },
  },
  issues: {
    PARENT_MISMATCH: 'the ID is not under its parent node / area (IDs are not reassigned)',
    NAME_VARIANT: 'name spelled differently for the same ID (the first spelling is kept)',
    UNRECOGNIZED_VALUE: 'unrecognised value (kept as text; check for shifted columns)',
    APPLICABILITY_REVIEW: 'vessel applicability needs review',
    INITIAL_INCOMPLETE: 'initial likelihood and severity must be given together',
    NOT_ASSESSED: 'not assessed: give initial likelihood and severity before activating',
    RESIDUAL_WITHOUT_INITIAL: 'residual risk given without an initial risk',
    DUPLICATE_LINK: 'the same hazard is repeated for the activity',
    INVALID_ISSUE_TYPE: 'issue type must be DUPLICATE or HAZARD REVIEW',
    INVALID_DATE: 'invalid date',
    APPROVAL_IGNORED:
      'approval in the file is ignored: activities are approved only in the shore review',
    REQUIRED: 'required value missing',
    TOO_LONG: 'text too long',
    INVALID_CODE: 'invalid code',
    DUPLICATE_CODE: 'duplicate code',
    UNKNOWN_VESSEL_GROUP: 'unknown vessel type',
    INVALID_STATUS: 'unknown status',
    INVALID_RA_TYPE: 'unknown RA type',
    INVALID_STAGE: 'unknown control stage',
    INVALID_HIERARCHY: 'unknown control hierarchy',
    INVALID_SCALE: 'likelihood and severity must be 1–5',
    INVALID_CONTROL_STATUS: 'unknown control status',
    SCORE_MISMATCH: 'score is not likelihood × severity',
    RESIDUAL_INCOMPLETE: 'residual likelihood and severity must be given together',
    RESIDUAL_ABOVE_INITIAL: 'residual risk above the initial risk',
    RESIDUAL_NOT_VERIFIED: 'residual risk given while controls are not verified',
    UNKNOWN_HAZARD: 'unknown hazard',
    UNKNOWN_CATEGORY: 'unknown operation area',
    UNKNOWN_ACTIVITY: 'unknown activity',
    UNKNOWN_CONTROL: 'unknown control',
    UNKNOWN_ROW: 'link without a risk row',
    LINK_MISSING: 'risk row without a relational link',
    LINK_MISMATCH: 'hazard of the link differs from the risk row',
    CONTROL_STAGE_MISMATCH: 'control stage does not match the column',
    MATRIX_INVALID: 'invalid risk band',
    TEXT_MISMATCH: 'text differs from the master record (the code is used)',
    APPLICABILITY_MISMATCH: 'vessel type differs from the activity (the activity’s is kept)',
    STEP_MISMATCH: 'task differs from the activity scope',
    COUNT_MISMATCH: 'count in START HERE differs from the file',
    INVALID: 'invalid value',
    OUT_OF_RANGE: 'out of range',
    TOO_FEW: 'at least one value is required',
    TOO_MANY: 'too many values',
    DUPLICATE: 'a value is repeated',
    DUPLICATE_ID: 'a step is repeated',
    GROUPS_ALL_EXCLUSIVE: '“All vessels” cannot be combined with other types',
  },
  sheets: {
    final00: 'FINAL 00 READ ME',
    final01: 'FINAL 01 Activity Import',
    final02: 'FINAL 02 Risk Import',
    final03: 'FINAL 03 Decision Queue',
    guide: 'START HERE',
    areas: '01 Areas',
    activities: '02 Activities',
    hazards: '03 Hazard Master',
    controls: '04 Control Library',
    rows: '05 RA Import',
    links: '06 Relational Links',
    matrix: '07 Matrix & Rules',
    vesselTypes: '08 Vessel Types',
  },
  errors: {
    INITIAL_INCOMPLETE:
      'Give both initial likelihood and severity, or leave both blank in a draft.',
    RESIDUAL_WITHOUT_INITIAL: 'Enter the initial risk before the residual risk.',
    REVIEW_TASKS_OPEN:
      'Open review queue items for this activity must be decided first: {{detail}}.',
    REASON_REQUIRED: 'Give the reason for the rejection in the note.',
    DECISION_REQUIRED: 'Write the decision before closing the item.',
    REVIEWER_REQUIRED: 'The reviewer is required.',
    REVIEW_DAY_REQUIRED: 'The review date is required.',
    NOT_UNDER_REVIEW: 'This activity is not under the FINAL technical review.',
    INVALID_TRANSITION: 'The record is already in this state.',
    ACTIVITY_RETIRED: 'A retired activity cannot be approved.',
    INVALID_DATE: 'Invalid date.',
    GENERIC: 'The request could not be completed. Try again.',
    NETWORK: 'No connection to the server.',
    FORBIDDEN: 'You are not authorised for this action.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    DUPLICATE_CODE: 'This code is already used.',
    CODE_IMMUTABLE: 'The code cannot be changed.',
    INVALID_RA_MASTER: 'Check the highlighted fields.',
    UNKNOWN_CATEGORY: 'Choose an operation area.',
    UNKNOWN_STEP: 'The task step is not in the activity.',
    STEP_IN_USE: 'This step is used by a risk row and cannot be removed.',
    UNKNOWN_HAZARD: 'Choose a hazard.',
    UNKNOWN_ACTIVITY: 'Choose an activity.',
    UNKNOWN_CONTROL: 'A control could not be found.',
    UNKNOWN_CONSEQUENCE: 'A consequence could not be found.',
    CONTROL_STAGE_MISMATCH:
      'Existing controls must have the Existing stage and additional controls the Additional stage.',
    CONTROL_LISTED_TWICE: 'A control cannot be both existing and additional.',
    RESIDUAL_INCOMPLETE: 'Give both residual likelihood and severity, or leave both blank.',
    RESIDUAL_ABOVE_INITIAL: 'The residual risk cannot be higher than the initial risk.',
    RESIDUAL_NOT_VERIFIED:
      'Residual risk is entered only after the controls are verified (control status other than “Not verified”).',
    DUPLICATE_LINK: 'This activity step already has this hazard.',
    SHEET_MISSING: 'A required sheet is missing: {{detail}}.',
    INVALID_HEADER: 'A required column is missing: {{detail}}.',
    INVALID_FILE: 'The file is not a valid Excel workbook.',
    EMPTY_FILE: 'The workbook has no sheets.',
    FILE_TOO_LARGE: 'The file is larger than 10 MB.',
    IMPORT_HAS_ERRORS: 'The file has errors; nothing was imported.',
  },
  defaultLibrary: {
    title: 'Default Risk Library',
    help: 'Felixmate default library {{version}}: {{count}} activities with proposed hazards and controls ({{inLibrary}} already in the company library). Choose the activities that fit your operation and add them to the company library.',
    draftNote:
      'Not approved: likelihood and severity are blank. Added activities enter the company library as drafts under technical review. Review them and enter the risk values before publishing.',
    search: 'Search activity, code or hazard',
    filter: {
      area: 'Operation Area',
      allAreas: 'All Operation Areas',
      node: 'System / Component',
      allNodes: 'All Systems / Components',
      group: 'Vessel Type',
      allGroups: 'All Vessel Types',
      show: 'Show',
      all: 'All Activities',
      available: 'Not in Company Library',
      inLibrary: 'In Company Library',
    },
    columns: {
      code: 'Activity ID',
      activity: 'Activity',
      area: 'Operation Area',
      groups: 'Vessel Type',
      hazards: 'Proposed Hazards',
      review: 'Review Queue',
      status: 'Status',
    },
    tableLabel: 'Default library activities',
    selectRow: 'Select {{code}}',
    selectPage: 'Select all on this page',
    selected: '{{count}} selected',
    selectMatching: 'Select All Matching ({{count}})',
    clear: 'Clear Selection',
    applicabilityReview: 'Applicability review',
    hazardCount_one: '{{count}} hazard',
    hazardCount_other: '{{count}} hazards',
    queue: { duplicate: 'Duplicate', hazard_review: 'Hazard review' },
    status: { inLibrary: 'In Library', available: 'Available' },
    add: 'Add to Company Library ({{count}})',
    cancel: 'Close',
    added:
      '{{count}} activities added to the company library as drafts. Review them and enter likelihood and severity before publishing.',
    nothingAdded: 'The selected activities are already in the company library.',
    empty: 'No activities match the filters',
    error: 'The default library could not be loaded',
    errors: {
      NOT_FOUND: 'An activity was not found in the default library: {{detail}}.',
      VALIDATION_FAILED: 'Select at least one activity.',
    },
  },
  library: {
    title: 'Start from the company library',
    help: 'Pick the activity; its hazards and controls are suggested from the company risk library.',
    activity: 'Library Activity',
    placeholder: 'Choose an activity',
    none: 'No library activity applies to this vessel yet.',
    hazards: 'Suggested hazards',
    select: 'Add {{hazard}}',
    controls: '{{existing}} existing · {{additional}} additional controls',
    add: 'Add Selected ({{count}})',
    added:
      '{{count}} hazards added from the library. Review them, then lower the residual risk where the controls are in place.',
    residualNote:
      'Residual risk starts equal to the initial risk. Lower it only after the controls are in place.',
    alreadyAdded: 'Already in this assessment',
  },
};

export const riskMasterTr: typeof riskMasterEn = {
  title: 'Risk Ana Verileri',
  description:
    'Şirketin risk kütüphanesi: operasyon alanları, faaliyetler, tehlikeler, sonuçlar, kontroller ve faaliyet–tehlike risk satırları. Ofiste yazılır, gemide salt okunur.',
  shipNote:
    'Ana veriler ofiste yönetilir ve gemide salt okunurdur. Gemi risk değerlendirmesini Risk Değerlendirmeleri ekranında bu faaliyetlerden başlatın.',
  stats: {
    activities: 'Faaliyetler',
    activitiesNote: '{{active}} etkin · {{draft}} taslak',
    hazards: 'Tehlikeler',
    hazardsNote: '{{count}} tehlike ailesi',
    controls: 'Kontroller',
    controlsNote: '{{existing}} mevcut · {{additional}} ek',
    links: 'Risk Satırları',
    linksNote: '{{high}} tanesi High ve üstü',
  },
  tabs: {
    queue: 'İnceleme Kuyruğu',
    categories: 'Operasyon Alanları',
    activities: 'Faaliyetler',
    hazards: 'Tehlikeler',
    consequences: 'Sonuçlar',
    controls: 'Kontroller',
    links: 'Risk Satırları',
  },
  tableLabel: '{{tab}} tablosu',
  search: 'Kod ya da ad ara',
  filter: {
    status: 'Durum',
    allStatuses: 'Bütün Durumlar',
    group: 'Gemi Tipi',
    allGroups: 'Bütün Gemi Tipleri',
    category: 'Operasyon Alanı',
    allCategories: 'Bütün Operasyon Alanları',
  },
  columns: {
    review: 'Teknik İnceleme',
    code: 'Kod',
    name: 'Ad',
    area: 'Operasyon Alanı',
    groups: 'Gemi Tipi',
    steps: 'Adım',
    types: 'RA Türü',
    status: 'Durum',
    family: 'Aile',
    category: 'Kategori',
    consequences: 'Sonuçlar',
    rows: 'Risk Satırı',
    stage: 'Aşama',
    hierarchy: 'Hiyerarşi',
    measure: 'Kontrol Önlemi',
    hazard: 'Tehlike',
    activity: 'Faaliyet',
    initial: 'İlk Risk',
    residual: 'Kalan Risk',
    controls: 'Kontroller',
    controlStatus: 'Kontrol Durumu',
    updated: 'Güncelleme',
    actions: 'İşlemler',
  },
  showing: '{{total}} kayıttan {{from}}–{{to}} gösteriliyor',
  pagination: 'Sayfalar',
  statuses: { draft: 'Taslak', active: 'Etkin', retired: 'Pasif' },
  groups: {
    ALL: 'Bütün gemiler',
    CARGO: 'Yük',
    TANKER: 'Tanker',
    YACHT_PASSENGER: 'Yat / Yolcu',
    OFFSHORE: 'Offshore',
  },
  hazardCategories: {
    mechanical: 'Mekanik',
    energy: 'Enerji',
    electrical: 'Elektrik',
    chemical: 'Kimyasal',
    environmental: 'Çevresel',
    human: 'İnsan',
    ergonomic: 'Ergonomik',
    navigation: 'Seyir',
    fire: 'Yangın',
    other: 'Diğer',
  },
  hierarchy: {
    elimination: 'Yok etme',
    substitution: 'İkame',
    engineering: 'Mühendislik',
    administrative: 'İdari',
    ppe: 'KKD',
  },
  stages: { existing: 'Mevcut', additional: 'Ek' },
  natures: { preventive: 'Önleyici', mitigative: 'Hafifletici' },
  requirements: { mandatory: 'Zorunlu', conditional: 'Koşullu' },
  controlStatuses: {
    not_verified: 'Doğrulanmadı',
    implemented: 'Uygulandı',
    not_implemented: 'Uygulanmadı',
    na_reviewed: 'Uygulanamaz – incelendi',
  },
  assessmentTypes: {
    routine: 'Rutin',
    task_specific: 'İşe özel',
    dynamic: 'Dinamik',
    change_related: 'Değişiklikle ilgili',
  },
  notAssessed: 'Değerlendirilmedi',
  review: {
    title: 'Teknik İnceleme (FINAL)',
    statuses: {
      draft: 'Taslak – onaylanmadı',
      in_review: 'İncelemede',
      approved: 'Onaylandı',
      rejected: 'Reddedildi',
      superseded: 'Yerine yenisi geldi',
    },
    approvedNote: 'Onaylı şablon gemiye önerilebilir; gemide işe özel RA yine gerekir.',
    notApprovedNote:
      'Yalnız ofiste görünür. Onaylanmadan gemiye ön incelenmiş şablon olarak sunulmaz.',
    node: 'Üst düğüm',
    applicability: 'Gemi uygunluğu',
    applicabilityReview: 'inceleme gerekli',
    pmsReview: 'PMS incelemesi',
    ptwScreening: 'PTW taraması',
    reviewQueue: 'Teknik inceleme kuyruğu',
    templateStatus: 'Şablon durumu (dosya)',
    source: 'Kaynak',
    reviewer: 'İnceleyen',
    reviewerHelp: 'Boş bırakılırsa sizin adınız yazılır.',
    reviewDay: 'İnceleme tarihi',
    reviewDayHelp: 'Boş bırakılırsa bugün.',
    note: 'Not',
    noteHelp: 'Retde zorunlu.',
    actions: {
      in_review: 'İncelemeye Al',
      superseded: 'Yerine Yenisi Geldi',
      rejected: 'Reddet',
      approved: 'Onayla',
    },
    saved: '{{code}}: {{status}}.',
  },
  queue: {
    title: 'İnceleme Kuyruğu',
    help: 'FINAL 03 karar kuyruğu: mükerrer ve tehlike incelemeleri. Kararı, inceleyeni ve tarihi kaydedin. Hiçbir şey kendiliğinden birleştirilmez.',
    search: 'Kod, faaliyet ya da gerekçe ara',
    type: 'Tür',
    status: 'Durum',
    allTypes: 'Bütün türler',
    allStatuses: 'Bütün durumlar',
    types: { duplicate: 'Mükerrer', hazard_review: 'Tehlike incelemesi' },
    statuses: { open: 'Açık', closed: 'Kapalı' },
    columns: {
      code: 'Kod',
      type: 'Tür',
      activity: 'Faaliyet',
      reason: 'Gerekçe',
      related: 'İlgili faaliyetler',
      status: 'Durum',
      decision: 'Karar',
    },
    open: '{{code}} aç',
    noMatch: 'Süzgece uyan inceleme kaydı yok.',
    empty: {
      title: 'İnceleme kaydı yok',
      description: 'Kuyruğu yüklemek için FINAL paketini içe aktarın.',
    },
    requiredAction: 'Gereken karar / işlem',
    noMerge:
      'Karar kaydetmek faaliyetleri birleştirmez ya da silmez. Birleştirme kararlaştırılırsa mükerrer kaydı Ana Veri’de pasifleyin; kimlikler korunur.',
    decision: 'Karar',
    reviewer: 'İnceleyen',
    reviewerHelp: 'Boş bırakılırsa sizin adınız yazılır.',
    reviewDay: 'Tarih',
    close: 'Kapat',
    reopen: 'Yeniden Aç',
    close_task: 'Kararı Kaydet ve Kapat',
    saved: '{{code}}: karar kaydedildi.',
  },
  none: '—',
  actions: {
    new: 'Yeni',
    import: 'Kütüphane İçe Aktar (Excel)',
    defaultLibrary: 'Varsayılan Kütüphane',
    activateDrafts: 'Taslakları Etkinleştir ({{count}})',
    activated: '{{count}} kayıt etkinleştirildi.',
    open: '{{code}} aç',
    edit: 'Düzenle',
    retire: 'Pasifleştir',
    reactivate: 'Yeniden Etkinleştir',
    activate: 'Etkinleştir',
    retired: '{{code}} pasifleştirildi. Kütüphanede ve geçmişte kalır.',
    reactivated: '{{code}} yeniden etkin.',
  },
  empty: {
    title: 'Henüz ana veri yok',
    office: 'Şirket kütüphanesini Excel’den içe aktarın ya da ilk kaydı ekleyin.',
    ship: 'Ofis henüz risk ana verisi yayımlamadı.',
    noMatch: 'Süzgeçlere uyan kayıt yok',
    error: 'Ana veriler yüklenemedi',
  },
  form: {
    newTitle: 'Yeni {{kind}}',
    editTitle: '{{code}}',
    viewTitle: '{{code}}',
    code: 'Kod',
    codeHelp: 'Excel içe aktarmasının doğal anahtarı. Sonradan değiştirilemez.',
    name: 'Ad',
    category: 'Operasyon Alanı',
    scope: 'İş / Kapsam',
    steps: 'İş Adımları',
    stepN: 'Adım {{n}}',
    addStep: 'Adım Ekle',
    removeStep: '{{n}}. adımı kaldır',
    groups: 'Gemi Tipi',
    types: 'RA Türü',
    ownerRole: 'Sahibi',
    status: 'Durum',
    family: 'Tehlike Ailesi Kodu',
    hazardCategory: 'Tehlike Kategorisi',
    suggested: 'Öneri: {{category}}',
    useSuggestion: 'Öneriyi kullan',
    mechanism: 'Mekanizma',
    consequences: 'Olası Sonuçlar',
    exposed: 'Maruz Kalan Kişi / Varlık',
    hazard: 'Tehlike',
    stage: 'Aşama',
    measure: 'Kontrol Önlemi',
    hierarchy: 'Kontrol Hiyerarşisi',
    hierarchyHelp: 'İlk seçilen değer birincildir.',
    nature: 'Önleyici / Hafifletici',
    requirement: 'Zorunlu / Koşullu',
    verificationMethod: 'Doğrulama Yöntemi',
    evidence: 'Kanıt',
    responsibleRole: 'Sorumlu Rol',
    activity: 'Faaliyet',
    step: 'İş Adımı',
    noStep: 'Bütün faaliyet',
    initial: 'İlk Risk',
    likelihood: 'Olasılık',
    severity: 'Şiddet',
    residual: 'Kalan Risk',
    residualHelp:
      'Kontroller uygulanıp doğrulanana dek boş bırakın. Kontroller “Doğrulanmadı” iken kalan risk girilemez.',
    existingControls: 'Mevcut Kontroller',
    additionalControls: 'Ek Kontroller',
    controlStatus: 'Kontrol Durumu',
    usedIn: '{{count}} risk satırında kullanılıyor',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    close: 'Kapat',
    saved: '{{code}} kaydedildi.',
    readOnly: 'Gemide salt okunur.',
  },
  import: {
    title: 'Risk Kütüphanesi İçe Aktar (Excel)',
    help: 'Şirket kütüphanesi çalışma kitabını yükleyin (01 Areas’tan 08 Vessel Types’a kadar sayfalar ya da FINAL 01–03 sayfalı FINAL teslim paketi). Kayıtlar kodlarıyla eşleşir: aynı dosya ikinci kez hiçbir şey yazmaz. Hiçbir şey silinmez; dosyada olmayan kayıtlar listelenir.',
    file: 'Excel Dosyası Seç',
    fileName: 'Dosya: {{name}}',
    preview: 'Önizle',
    apply: 'İçe Aktar',
    cancel: 'Kapat',
    summary: 'Önizleme',
    data: 'Veri',
    add: 'Yeni',
    update: 'Güncellenen',
    same: 'Aynı',
    errors: 'Hatalar ({{count}}): dosyada düzeltilene dek hiçbir şey içe aktarılmaz.',
    warnings: 'Uyarılar ({{count}})',
    missing: 'Kütüphanede olup dosyada olmayan ({{count}}): korunur, silinmez.',
    changes: 'Değişen alanlar',
    more: '… ve {{count}} tane daha',
    issue: '{{sheet}} · satır {{row}} · {{field}}: {{message}}',
    matrix: {
      current: 'Risk bantları (07) kullanılan matrisle aynı: {{name}}.',
      other:
        'Risk bantları (07) kullanılmayan “{{name}}” profiliyle aynı. Gerekirse Ayarlar ▸ Risk Matrisi’nden değiştirin.',
      not_found:
        'Risk bantları (07) hiçbir risk matrisi profiliyle aynı değil. Matris içe aktarılmaz; gerekirse Ayarlar ▸ Risk Matrisi’nde yayımlayın.',
    },
    statusNote:
      'Yeni kayıtlar dosyadaki durumla (Taslak) gelir. Ofiste verilmiş durumlar ve doğrulanmış kalan riskler korunur.',
    applied: 'Kütüphane içe aktarıldı. Yeni {{add}}, güncellenen {{update}}.',
    nothingToDo: 'Kütüphane güncel: içe aktarılacak bir şey yok.',
    final: {
      kinds: {
        categories: 'Alanlar (Area ID)',
        activities: 'Faaliyetler (TASK)',
        hazards: 'Tehlikeler',
        consequences: 'Sonuçlar',
        controls: 'Önerilen kontroller',
        links: 'Risk adayları',
      },
      queue: 'İnceleme kuyruğu (FINAL 03)',
      summary:
        'FINAL teslim paketi: {{nodes}} üst düğüm (yalnız gruplama, seçilemez); {{review}} faaliyetin gemi uygunluğu incelenmeli.',
      reopened: '{{count}} onaylı faaliyet değişti ve yeniden incelemeye döner: {{codes}}.',
      note: 'Her şey kıyı teknik incelemesi için taslak gelir: dosya hiçbir şeyi onaylamaz, olasılık ve şiddet boş kalır (varsayılan yok), mükerrerler birleştirilmez. Kimlikler korunur; hiçbir şey silinmez.',
    },
  },
  issues: {
    PARENT_MISMATCH: 'kimlik üst düğümün / alanın altında değil (kimlik yeniden atanmaz)',
    NAME_VARIANT: 'aynı kimlik için farklı yazılış (ilk yazılış kalır)',
    UNRECOGNIZED_VALUE: 'tanınmayan değer (metin olarak saklanır; kaymış sütunu kontrol edin)',
    APPLICABILITY_REVIEW: 'gemi uygunluğu incelenmeli',
    INITIAL_INCOMPLETE: 'başlangıç olasılık ve şiddeti birlikte girilir',
    NOT_ASSESSED: 'değerlendirilmedi: etkinleştirmeden önce başlangıç olasılık ve şiddetini girin',
    RESIDUAL_WITHOUT_INITIAL: 'başlangıç riski olmadan kalan risk girilmiş',
    DUPLICATE_LINK: 'faaliyette aynı tehlike tekrar ediyor',
    INVALID_ISSUE_TYPE: 'tür DUPLICATE ya da HAZARD REVIEW olmalı',
    INVALID_DATE: 'geçersiz tarih',
    APPROVAL_IGNORED: 'dosyadaki onay yok sayılır: faaliyet yalnız kıyı incelemesinde onaylanır',
    REQUIRED: 'zorunlu değer eksik',
    TOO_LONG: 'metin çok uzun',
    INVALID_CODE: 'geçersiz kod',
    DUPLICATE_CODE: 'kod tekrar ediyor',
    UNKNOWN_VESSEL_GROUP: 'bilinmeyen gemi tipi',
    INVALID_STATUS: 'bilinmeyen durum',
    INVALID_RA_TYPE: 'bilinmeyen RA türü',
    INVALID_STAGE: 'bilinmeyen kontrol aşaması',
    INVALID_HIERARCHY: 'bilinmeyen kontrol hiyerarşisi',
    INVALID_SCALE: 'olasılık ve şiddet 1–5 olmalı',
    INVALID_CONTROL_STATUS: 'bilinmeyen kontrol durumu',
    SCORE_MISMATCH: 'skor olasılık × şiddet değil',
    RESIDUAL_INCOMPLETE: 'kalan olasılık ve şiddet birlikte verilmeli',
    RESIDUAL_ABOVE_INITIAL: 'kalan risk ilk riskten büyük',
    RESIDUAL_NOT_VERIFIED: 'kontroller doğrulanmadan kalan risk girilmiş',
    UNKNOWN_HAZARD: 'bilinmeyen tehlike',
    UNKNOWN_CATEGORY: 'bilinmeyen operasyon alanı',
    UNKNOWN_ACTIVITY: 'bilinmeyen faaliyet',
    UNKNOWN_CONTROL: 'bilinmeyen kontrol',
    UNKNOWN_ROW: 'risk satırı olmayan bağ',
    LINK_MISSING: 'bağı olmayan risk satırı',
    LINK_MISMATCH: 'bağın tehlikesi risk satırınınkinden farklı',
    CONTROL_STAGE_MISMATCH: 'kontrolün aşaması sütunla uyuşmuyor',
    MATRIX_INVALID: 'geçersiz risk bandı',
    TEXT_MISMATCH: 'metin ana kayıttan farklı (kod kullanılır)',
    APPLICABILITY_MISMATCH: 'gemi tipi faaliyetinkinden farklı (faaliyetinki korunur)',
    STEP_MISMATCH: 'iş, faaliyetin kapsamından farklı',
    COUNT_MISMATCH: 'START HERE’deki sayı dosyayla uyuşmuyor',
    INVALID: 'geçersiz değer',
    OUT_OF_RANGE: 'aralık dışında',
    TOO_FEW: 'en az bir değer gerekli',
    TOO_MANY: 'çok fazla değer',
    DUPLICATE: 'bir değer tekrar ediyor',
    DUPLICATE_ID: 'bir adım tekrar ediyor',
    GROUPS_ALL_EXCLUSIVE: '“Bütün gemiler” başka tiplerle birlikte seçilemez',
  },
  sheets: riskMasterEn.sheets,
  errors: {
    INITIAL_INCOMPLETE:
      'Başlangıç olasılık ve şiddetini birlikte girin ya da taslakta ikisini de boş bırakın.',
    RESIDUAL_WITHOUT_INITIAL: 'Kalan riskten önce başlangıç riskini girin.',
    REVIEW_TASKS_OPEN:
      'Bu faaliyetin açık inceleme kuyruğu kayıtları önce karara bağlanmalı: {{detail}}.',
    REASON_REQUIRED: 'Ret gerekçesini notta yazın.',
    DECISION_REQUIRED: 'Kaydı kapatmadan önce kararı yazın.',
    REVIEWER_REQUIRED: 'İnceleyen zorunlu.',
    REVIEW_DAY_REQUIRED: 'İnceleme tarihi zorunlu.',
    NOT_UNDER_REVIEW: 'Bu faaliyet FINAL teknik incelemesinde değil.',
    INVALID_TRANSITION: 'Kayıt zaten bu durumda.',
    ACTIVITY_RETIRED: 'Pasif faaliyet onaylanamaz.',
    INVALID_DATE: 'Geçersiz tarih.',
    GENERIC: 'İstek tamamlanamadı. Yeniden deneyin.',
    NETWORK: 'Sunucuya bağlantı yok.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yenileyip yeniden deneyin.',
    DUPLICATE_CODE: 'Bu kod kullanılıyor.',
    CODE_IMMUTABLE: 'Kod değiştirilemez.',
    INVALID_RA_MASTER: 'İşaretli alanları kontrol edin.',
    UNKNOWN_CATEGORY: 'Bir operasyon alanı seçin.',
    UNKNOWN_STEP: 'İş adımı faaliyette yok.',
    STEP_IN_USE: 'Bu adım bir risk satırında kullanılıyor, kaldırılamaz.',
    UNKNOWN_HAZARD: 'Bir tehlike seçin.',
    UNKNOWN_ACTIVITY: 'Bir faaliyet seçin.',
    UNKNOWN_CONTROL: 'Bir kontrol bulunamadı.',
    UNKNOWN_CONSEQUENCE: 'Bir sonuç bulunamadı.',
    CONTROL_STAGE_MISMATCH: 'Mevcut kontroller “Mevcut”, ek kontroller “Ek” aşamalı olmalı.',
    CONTROL_LISTED_TWICE: 'Bir kontrol hem mevcut hem ek olamaz.',
    RESIDUAL_INCOMPLETE: 'Kalan olasılık ve şiddeti birlikte verin ya da ikisini de boş bırakın.',
    RESIDUAL_ABOVE_INITIAL: 'Kalan risk ilk riskten yüksek olamaz.',
    RESIDUAL_NOT_VERIFIED:
      'Kalan risk yalnız kontroller doğrulandıktan sonra girilir (kontrol durumu “Doğrulanmadı” dışında).',
    DUPLICATE_LINK: 'Bu faaliyet adımında bu tehlike zaten var.',
    SHEET_MISSING: 'Zorunlu bir sayfa eksik: {{detail}}.',
    INVALID_HEADER: 'Zorunlu bir sütun eksik: {{detail}}.',
    INVALID_FILE: 'Dosya geçerli bir Excel çalışma kitabı değil.',
    EMPTY_FILE: 'Çalışma kitabında sayfa yok.',
    FILE_TOO_LARGE: 'Dosya 10 MB’tan büyük.',
    IMPORT_HAS_ERRORS: 'Dosyada hata var; hiçbir şey içe aktarılmadı.',
  },
  defaultLibrary: {
    title: 'Varsayılan Risk Kütüphanesi',
    help: 'Felixmate varsayılan kütüphanesi {{version}}: önerilen tehlike ve kontrolleriyle {{count}} faaliyet ({{inLibrary}} tanesi şirket kütüphanesinde). İşinize uyan faaliyetleri seçip şirket kütüphanesine ekleyin.',
    draftNote:
      'Onaylı değildir: olasılık ve şiddet boştur. Eklenen faaliyetler şirket kütüphanesine teknik incelemede taslak olarak girer. Yayımlamadan önce inceleyin ve risk değerlerini girin.',
    search: 'Faaliyet, kod ya da tehlike ara',
    filter: {
      area: 'Operasyon Alanı',
      allAreas: 'Tüm Operasyon Alanları',
      node: 'Sistem / Bileşen',
      allNodes: 'Tüm Sistemler / Bileşenler',
      group: 'Gemi Tipi',
      allGroups: 'Tüm Gemi Tipleri',
      show: 'Göster',
      all: 'Tüm Faaliyetler',
      available: 'Şirket Kütüphanesinde Olmayan',
      inLibrary: 'Şirket Kütüphanesinde Olan',
    },
    columns: {
      code: 'Faaliyet No',
      activity: 'Faaliyet',
      area: 'Operasyon Alanı',
      groups: 'Gemi Tipi',
      hazards: 'Önerilen Tehlikeler',
      review: 'Karar Kuyruğu',
      status: 'Durum',
    },
    tableLabel: 'Varsayılan kütüphane faaliyetleri',
    selectRow: '{{code}} seç',
    selectPage: 'Bu sayfadakilerin hepsini seç',
    selected: '{{count}} seçili',
    selectMatching: 'Süzülenlerin Hepsini Seç ({{count}})',
    clear: 'Seçimi Temizle',
    applicabilityReview: 'Uygunluk incelemesi',
    hazardCount_one: '{{count}} tehlike',
    hazardCount_other: '{{count}} tehlike',
    queue: { duplicate: 'Mükerrer', hazard_review: 'Tehlike incelemesi' },
    status: { inLibrary: 'Kütüphanede', available: 'Eklenebilir' },
    add: 'Şirket Kütüphanesine Ekle ({{count}})',
    cancel: 'Kapat',
    added:
      '{{count}} faaliyet şirket kütüphanesine taslak olarak eklendi. Yayımlamadan önce inceleyin, olasılık ve şiddeti girin.',
    nothingAdded: 'Seçilen faaliyetler zaten şirket kütüphanesinde.',
    empty: 'Süzgeçlere uyan faaliyet yok',
    error: 'Varsayılan kütüphane yüklenemedi',
    errors: {
      NOT_FOUND: 'Faaliyet varsayılan kütüphanede bulunamadı: {{detail}}.',
      VALIDATION_FAILED: 'En az bir faaliyet seçin.',
    },
  },
  library: {
    title: 'Şirket kütüphanesinden başla',
    help: 'Faaliyeti seçin; tehlikeleri ve kontrolleri şirket risk kütüphanesinden önerilir.',
    activity: 'Kütüphane Faaliyeti',
    placeholder: 'Faaliyet seçin',
    none: 'Bu gemiye uygulanan kütüphane faaliyeti henüz yok.',
    hazards: 'Önerilen tehlikeler',
    select: '{{hazard}} ekle',
    controls: '{{existing}} mevcut · {{additional}} ek kontrol',
    add: 'Seçilenleri Ekle ({{count}})',
    added:
      'Kütüphaneden {{count}} tehlike eklendi. Gözden geçirin; kontroller yerindeyse kalan riski düşürün.',
    residualNote: 'Kalan risk ilk riske eşit başlar. Yalnız kontroller yerindeyse düşürün.',
    alreadyAdded: 'Bu değerlendirmede zaten var',
  },
};
